// @ts-check
import { useEffect, useRef, useState } from "react";

/**
 * @template Meta
 * @typedef {"ready" | "failed" | "timeout" | "error"} PolledJobOutcome
 */

/**
 * Read the in-flight job persisted under `storageKey`, as `{ jobId, meta }`.
 *
 * @template Meta
 * @param {string} storageKey
 * @returns {{ jobId: string, meta?: Meta } | null} The stored job, or null when
 *   none is stored or the entry is unreadable.
 */
function readStoredJob(storageKey) {
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    return parsed?.jobId ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * @template Meta
 * @param {string} storageKey
 * @param {string} jobId
 * @param {Meta} [meta]
 */
function writeStoredJob(storageKey, jobId, meta) {
  try {
    sessionStorage.setItem(storageKey, JSON.stringify({ jobId, meta }));
  } catch {
    // A storage write failure only costs refresh-resume for this job; the job
    // still runs and settles in the current session.
  }
}

/**
 * @param {string} storageKey
 */
function clearStoredJob(storageKey) {
  try {
    sessionStorage.removeItem(storageKey);
  } catch {
    // no-op: nothing to clean up if storage is unavailable.
  }
}

/**
 * Headless lifecycle for a server-side job that is submitted once and then
 * polled to a terminal state, with a persistent-toast-style surface driven
 * through callbacks. It is surface-agnostic (the caller decides whether a
 * terminal outcome shows a toast, a modal, or nothing) and transport-agnostic
 * (the caller owns the submit; this hook is handed the job id once it exists).
 *
 * The lifecycle, once {@link PolledJobControls.track} is called with a job id:
 * - `onStart` fires so the caller can show an in-progress affordance; the job
 *   is persisted to `sessionStorage` so a page refresh resumes it;
 * - on mount, any job persisted under `storageKey` is picked back up: `onStart`
 *   fires and polling restarts (nothing is auto-actioned, so a resumed job never
 *   surprises the viewer);
 * - `useStatus` is polled until `getStatus` reports `ready` or `failed`;
 * - `ready` / `failed` → `onSettled` with that outcome;
 * - a status poll that keeps failing past `tolerance` consecutive cycles →
 *   `onSettled` with `error` (a transient blip is polled through and self-heals
 *   on the next success, so only a sustained streak abandons the job);
 * - past `ceilingMs` → `onSettled` with `timeout`.
 *
 * Every terminal outcome fires `onSettled` exactly once, closes out the stored
 * job, and stops polling.
 *
 * @template {object} Data - The shape `useStatus` yields as its `data`.
 * @template Meta - Opaque per-job payload the caller threads through (persisted
 *   with the job and handed back on every callback); e.g. a download filename.
 *
 * @param {object} params
 * @param {(jobId: string | null) => { data?: Data, isError?: boolean, errorUpdatedAt?: number }} params.useStatus
 *   Status-poll hook, called every render with the tracked job id (null when
 *   idle, so it must disable itself on a null id). Keeps polling on error.
 * @param {(data: Data | undefined) => ("ready" | "failed" | undefined | null | "" | false)} params.getStatus
 *   Maps a poll result to a terminal signal; a falsy return means "still
 *   pending".
 * @param {string} params.storageKey - `sessionStorage` key holding the in-flight
 *   job. Session-scoped on purpose: it survives a refresh but not the end of the
 *   browsing session.
 * @param {(meta: Meta | undefined) => void} params.onStart - A job began tracking
 *   (a fresh `track` or a refresh-resume).
 * @param {(settled: { outcome: "ready" | "failed" | "timeout" | "error", jobId: string, data?: Data, meta?: Meta }) => void} params.onSettled
 *   A terminal outcome, fired once per job.
 * @param {number} [params.tolerance] - Consecutive failed poll cycles tolerated
 *   before `error`. Defaults to 3.
 * @param {number} params.ceilingMs - Client poll ceiling, measured from when
 *   polling (re)starts, so a refresh restarts the clock. Guards a status
 *   endpoint that never reaches a terminal state so polling cannot run forever.
 * @returns {{ track: (jobId: string, meta?: Meta) => void }} `track` begins the
 *   lifecycle for a submitted job.
 */
export function usePolledJob({
  useStatus,
  getStatus,
  storageKey,
  onStart,
  onSettled,
  tolerance = 3,
  ceilingMs,
}) {
  const [jobId, setJobId] = useState(/** @type {string|null} */ (null));

  // Opaque per-job payload, held so it can be handed back on the terminal
  // callback and the ceiling timeout without re-deriving it.
  const metaRef =
    /** @type {import("react").MutableRefObject<Meta|undefined>} */ (useRef());
  // Guards terminal handling so an outcome is acted on once per job, even though
  // its effect re-runs while the query keeps polling.
  const handledRef = useRef(false);
  // Mirrors "a job is being tracked" synchronously (state lags a render), so the
  // mount-resume can tell a just-started job from a clean load.
  const trackingRef = useRef(false);
  // Ensures the mount-time resume runs once even under StrictMode double-invoke.
  const resumeAttemptedRef = useRef(false);
  // Counts consecutive failed poll cycles (by their error timestamp) so a
  // transient blip is tolerated and only a sustained streak abandons the job.
  const statusErrorStreakRef = useRef(0);
  const lastStatusErrorAtRef = useRef(0);

  // Keep the callbacks and mapper in refs so the effects can depend on the job
  // state alone and never fire on an inline-callback identity change.
  const onStartRef = useRef(onStart);
  const onSettledRef = useRef(onSettled);
  const getStatusRef = useRef(getStatus);
  onStartRef.current = onStart;
  onSettledRef.current = onSettled;
  getStatusRef.current = getStatus;

  // Begin tracking a job: reset per-job guard state, persist it for refresh
  // resume, enable polling, and let the caller show its in-progress affordance.
  const track = (id, meta) => {
    handledRef.current = false;
    trackingRef.current = true;
    statusErrorStreakRef.current = 0;
    lastStatusErrorAtRef.current = 0;
    metaRef.current = meta;
    writeStoredJob(storageKey, id, meta);
    setJobId(id);
    onStartRef.current?.(meta);
  };

  // End tracking a job: mark it handled, clear the stored job, and disable
  // polling. The caller's terminal callback runs separately.
  const finish = () => {
    handledRef.current = true;
    trackingRef.current = false;
    clearStoredJob(storageKey);
    setJobId(null);
  };

  // On mount, resume a job left in flight by an earlier page load. A fresh
  // `track` on this same mount takes precedence; there is no in-flight job on a
  // clean load.
  useEffect(() => {
    if (resumeAttemptedRef.current) {
      return;
    }
    resumeAttemptedRef.current = true;
    if (trackingRef.current) {
      return;
    }
    const stored = /** @type {{ jobId: string, meta?: Meta }|null} */ (
      readStoredJob(storageKey)
    );
    if (stored) {
      track(stored.jobId, stored.meta);
    }
    // Runs once on mount; helpers are stable enough for a one-shot resume.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusQuery = useStatus(jobId);
  const statusData = statusQuery.data;
  const status = getStatusRef.current(statusData);
  const statusErrored = statusQuery.isError;
  const statusErrorAt = statusQuery.errorUpdatedAt ?? 0;

  // Client poll ceiling: once a job has run past ceilingMs, stop polling
  // (clearing the job disables the query) and settle as a timeout.
  useEffect(() => {
    if (!jobId) {
      return;
    }
    const trackedJobId = jobId;
    const timer = setTimeout(() => {
      finish();
      onSettledRef.current({
        outcome: "timeout",
        jobId: trackedJobId,
        meta: metaRef.current,
      });
    }, ceilingMs);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  // Act on a terminal outcome: `ready`/`failed` settle immediately; a generic
  // `error` settles only when the status poll keeps failing past the tolerance
  // (a 4xx/5xx streak that never yields a status).
  useEffect(() => {
    if (!jobId || handledRef.current) {
      return;
    }
    if (status === "ready") {
      const settledJobId = jobId;
      const settledMeta = metaRef.current;
      finish();
      onSettledRef.current({
        outcome: "ready",
        jobId: settledJobId,
        data: statusData,
        meta: settledMeta,
      });
    } else if (status === "failed") {
      const settledJobId = jobId;
      const settledMeta = metaRef.current;
      finish();
      onSettledRef.current({
        outcome: "failed",
        jobId: settledJobId,
        data: statusData,
        meta: settledMeta,
      });
    } else if (statusErrored) {
      // Count each distinct failed cycle once (by its error timestamp); the
      // query keeps polling, so a transient blip self-heals on the next success
      // and only a sustained streak abandons the job.
      if (statusErrorAt !== lastStatusErrorAtRef.current) {
        lastStatusErrorAtRef.current = statusErrorAt;
        statusErrorStreakRef.current += 1;
      }
      if (statusErrorStreakRef.current >= tolerance) {
        const settledJobId = jobId;
        const settledMeta = metaRef.current;
        finish();
        onSettledRef.current({
          outcome: "error",
          jobId: settledJobId,
          meta: settledMeta,
        });
      }
    } else if (status) {
      // A successful poll with a non-terminal status resets the error streak.
      statusErrorStreakRef.current = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, status, statusErrored, statusErrorAt, statusData]);

  return { track };
}
